import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Document, pdfjs, Thumbnail } from "react-pdf";
import { currencyDeformatter, currencyFormatter, currencyInputHandling } from "../../hooks-n-utils/currency";
import { FiExternalLink } from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import { FaHashtag } from "react-icons/fa";
import { IoReceipt, IoCloseCircle } from "react-icons/io5";
import { HiOutlineArrowLeft } from "react-icons/hi";
import { AiFillPlusCircle } from "react-icons/ai";
import { BsArrowsAngleExpand } from "react-icons/bs";
import { TbSquareRoundedNumber5, TbReceiptFilled } from "react-icons/tb";
import { useAuth } from "../../contexts/AuthContext";
import { notify } from "../../hooks-n-utils/toast";
import { insuranceAssetProvider } from "../../hooks-n-utils/insurance-assets";
import { generateUID } from "../../hooks-n-utils/uid-generator";

const ExpenseForm = ({ selectedExpense, setSelectedExpense }) => {
  const {
    currentUser,
    colorMode,
    addExpense,
    addExpenseLoading,
    updateExpense,
    updateExpenseLoading,
    deleteExpense,
    deleteExpenseLoading,
    hsaExpenseGuide,
    hasGuide,
  } = useAuth();

  const navigate = useNavigate();

  pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

  const [name, setName] = useState("");
  const [date, setDate] = useState("");
  const [year, setYear] = useState("");
  const [maxDate, setMaxDate] = useState("");
  const [minDate, setMinDate] = useState("");
  const [currentDate, setCurrentDate] = useState("");
  const [amount, setAmount] = useState(0);
  const [category, setCategory] = useState("");
  const [eligible, setEligible] = useState(true);
  const [underInsurance, setUnderInsurance] = useState(false);
  const [insuranceToggle, setInsuranceToggle] = useState(false);
  const [selectedInsurance, setSelectedInsurance] = useState("");
  const [selectedLogo, setSelectedLogo] = useState("");
  const [isCustomInsurance, setIsCustomInsurance] = useState(false);
  const [withdrawn, setWithdrawn] = useState(false);
  const [dateWithdrawn, setDateWithdrawn] = useState("");
  const [tags, setTags] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [inputKey, setInputKey] = useState(0);
  const [notes, setNotes] = useState("");
  const [initialDelete, setInitialDelete] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  const [expenseDateError, setExpenseDateError] = useState("");
  const [amountError, setAmountError] = useState("");
  const [widthdrawnDateError, setWithdrawnDateError] = useState("");
  const [fileError, setFileError] = useState("");

  const [base64, setBase64] = useState(null);

  const [addingTags, setAddingTags] = useState(false);
  const tagRef = useRef();

  const expenseFormRef = useRef(null);

  const [expandedReceipt, setExpandedReceipt] = useState("");

  const expenseCategories = [
    "Doctor Visit",
    "Medication",
    "Dental Care",
    "Vision Care",
    "Mental Health",
    "Hospital Services",
    "Physical Therapy / Rehabilitation",
    "Medical Supplies",
  ];

  const pickInsurance = (provider) => {
    setSelectedInsurance(null);
    document.getElementById("custom-ins-input").value = null;
    setIsCustomInsurance(false);
    setSelectedInsurance(provider);
    setSelectedLogo(insuranceAssetProvider(provider, colorMode));
  };

  const handleManualInsuranceEntry = (entry) => {
    if (!isCustomInsurance) {
      setIsCustomInsurance(true);
    }
    setSelectedInsurance(entry);
  };

  const resetForm = () => {
    setName("");
    setDate("");
    setYear("");
    setMaxDate("");
    setMinDate("");
    setCurrentDate("");
    setAmount(0);
    setCategory("");
    setEligible(true);
    setUnderInsurance(false);
    setInsuranceToggle(false);
    setSelectedInsurance("");
    setSelectedLogo("");
    setIsCustomInsurance(false);
    setWithdrawn(false);
    setDateWithdrawn("");
    setTags([]);
    setAddingTags(false);
    setReceipts([]);
    setInputKey(0);
    setNotes("");
    setInitialDelete(false);
    setExpenseDateError("");
    setAmountError("");
    setWithdrawnDateError("");
    expenseFormRef.current.scroll({
      top: 0,
      behavior: "instant",
    });
  };

  const mapExpenseValues = (selectedExpense) => {
    setName(selectedExpense.name);
    setDate(selectedExpense.date);
    setYear(selectedExpense.year);
    setAmount(currencyFormatter(selectedExpense.amount));
    setCategory(selectedExpense.category);
    setEligible(selectedExpense.eligible);
    setUnderInsurance(selectedExpense.underInsurance);
    setSelectedInsurance(selectedExpense.selectedInsurance);
    if (selectedExpense.underInsurance) {
      if (selectedExpense.isCustomInsurance) {
        handleManualInsuranceEntry(selectedExpense.selectedInsurance);
      } else {
        pickInsurance(selectedExpense.selectedInsurance);
      }
    }
    setWithdrawn(selectedExpense.withdrawn);
    setDateWithdrawn(selectedExpense.dateWithdrawn ? selectedExpense.dateWithdrawn : "");
    setTags(selectedExpense.tags);
    setReceipts(selectedExpense.receipts);
    setNotes(selectedExpense.notes);
  };

  const handleExpenseAction = (type) => {
    if (type === "delete" && !selectedExpense) return; // Handle deletion only if an expense is selected

    let id;
    let metadata;

    if (selectedExpense) {
      id = selectedExpense.id;
      metadata = {
        createdDate: selectedExpense.metadata.createdDate,
        lastUpdatedDate: new Date(),
      };
    } else {
      let today = new Date();
      id = generateUID("expense", today.getFullYear());
      metadata = {
        createdDate: new Date(),
        lastUpdatedDate: "",
      };
    }

    let payload = {
      id: id,
      name: name !== undefined ? name : selectedExpense?.name || "",
      date: date !== undefined ? date : selectedExpense?.date || "",
      year: year !== undefined ? year : selectedExpense?.year || "",
      amount: currencyDeformatter(amount !== undefined ? amount : selectedExpense?.amount || 0),
      category: category !== undefined ? category : selectedExpense?.category || "",
      eligible: eligible !== undefined ? eligible : selectedExpense?.eligible || false,
      underInsurance: underInsurance !== undefined ? underInsurance : selectedExpense?.underInsurance || false,
      isCustomInsurance:
        isCustomInsurance !== undefined ? isCustomInsurance : selectedExpense?.isCustomInsurance || false,
      selectedInsurance: selectedInsurance !== undefined ? selectedInsurance : selectedExpense?.selectedInsurance || "",
      withdrawn: withdrawn !== undefined ? withdrawn : selectedExpense?.withdrawn || false,
      dateWithdrawn: dateWithdrawn !== undefined ? dateWithdrawn : selectedExpense?.dateWithdrawn || "",
      tags: tags !== undefined ? tags : selectedExpense?.tags || [],
      receipts: receipts !== undefined ? receipts : selectedExpense?.receipts || [],
      notes: notes !== undefined ? notes : selectedExpense?.notes || "",
      metadata: metadata,
    };

    const formActions = {
      save: () => {
        addExpense(payload, currentUser?.uid).then(() => {
          navigate("/expenses");
          document
            .getElementById("expense-form-drawer")
            ?.hide()
            .then(() => resetForm());
          notify("Your expense has been saved!", "success");
        });
      },
      update: () => {
        if (!isFormValid) {
          notify("Your form is incomplete. Please check that each required field is filled out.", "danger");
        } else {
          updateExpense(payload, selectedExpense?.year, currentUser?.uid).then(() => {
            setSelectedExpense(payload);
            navigate(`/expenses/details/${payload.id}`);
            resetForm();
            document.getElementById("expense-form-drawer")?.hide();
            mapExpenseValues(selectedExpense);
            notify("Your expense has been updated.", "update");
          });
        }
      },
      delete: () => {
        deleteExpense(payload, currentUser?.uid).then(() => {
          setSelectedExpense(null);
          document
            .getElementById("expense-form-drawer")
            ?.hide()
            .then(() => resetForm());
          notify(`'${payload.name}' expense has been deleted`, "danger");
        });
      },
    };

    if (formActions[type]) formActions[type]();
  };

  useEffect(() => {
    // Prevent the dialog from closing when the user clicks on the overlay
    window.addEventListener("sl-request-close", (event) => {
      if (event.detail.source === "overlay" && event.target.id === "expense-form-drawer") {
        event.preventDefault();
      }
    });

    // Listens for selects to be cleared (just category in this case)
    window.addEventListener("sl-clear", (event) => {
      if (event.target.id === "expense-category-input") {
        setCategory("");
      }
    });

    return () => {
      window.removeEventListener("sl-request-close", (event) => {
        if (event.detail.source === "overlay" && event.target.id === "expense-form-drawer") {
          event.preventDefault();
        }
      });
      window.removeEventListener("sl-clear", (e) => {});
    };
  }, []);

  useEffect(() => {
    if (Boolean(selectedExpense)) {
      mapExpenseValues(selectedExpense);
    } else {
      resetForm();
    }
  }, [selectedExpense]);

  useEffect(() => {
    setCurrentDate(new Date().toJSON().slice(0, 10));
    let expenseMaxDate = new Date();
    expenseMaxDate.setMonth(expenseMaxDate.getMonth() + 6);
    setMaxDate(expenseMaxDate.toJSON().slice(0, 10));
    let expenseMinDate = new Date(2004, 0, 1);
    setMinDate(expenseMinDate.toJSON().slice(0, 10));

    if (receipts.length > 4) {
      document.getElementById("add-reciept-btn")?.classList?.add("disabled");
    } else {
      document.getElementById("add-reciept-btn")?.classList?.remove("disabled");
    }

    if (
      name &&
      date &&
      year &&
      amount &&
      category &&
      ((underInsurance && selectedInsurance && !insuranceToggle) || !underInsurance) &&
      ((withdrawn && dateWithdrawn) || !withdrawn) &&
      !expenseDateError &&
      !amountError &&
      !widthdrawnDateError
    ) {
      if (initialDelete) {
        document.querySelector(".save-action")?.removeAttribute("disabled");
        document.querySelector(".update-cancel-action")?.removeAttribute("disabled");
        setIsFormValid(true);
      } else if (selectedExpense) {
        let noChange = Boolean(
          name === selectedExpense.name &&
            date === selectedExpense.date &&
            currencyDeformatter(amount) === currencyDeformatter(selectedExpense.amount) &&
            category === selectedExpense.category &&
            eligible === selectedExpense.eligible &&
            underInsurance === selectedExpense.underInsurance &&
            selectedInsurance === selectedExpense.selectedInsurance &&
            withdrawn === selectedExpense.withdrawn &&
            (dateWithdrawn ? dateWithdrawn : "") === selectedExpense.dateWithdrawn &&
            tags === selectedExpense.tags &&
            receipts === selectedExpense.receipts &&
            notes === selectedExpense.notes
        );
        if (noChange) {
          document.querySelector(".save-action")?.setAttribute("disabled", true);
          document.querySelector(".update-cancel-action")?.setAttribute("disabled", true);
          setIsFormValid(false);
        } else {
          document.querySelector(".save-action")?.removeAttribute("disabled");
          document.querySelector(".update-cancel-action")?.removeAttribute("disabled");
          setIsFormValid(true);
        }
      } else {
        document.querySelector(".save-action")?.removeAttribute("disabled");
        document.querySelector(".update-cancel-action")?.removeAttribute("disabled");
        setIsFormValid(true);
      }
    } else {
      document.querySelector(".save-action")?.setAttribute("disabled", true);
      document.querySelector(".update-cancel-action")?.setAttribute("disabled", true);
      setIsFormValid(false);
    }

    if (selectedExpense && !selectedExpense?.underInsurance) {
      setSelectedInsurance("");
      document.getElementById("custom-ins-input").value = null;
      setIsCustomInsurance(false);
      setSelectedLogo();
    }
  }, [
    currentUser,
    selectedExpense,
    hasGuide,
    name,
    date,
    amount,
    year,
    category,
    eligible,
    underInsurance,
    selectedInsurance,
    insuranceToggle,
    withdrawn,
    dateWithdrawn,
    expenseDateError,
    widthdrawnDateError,
    tags,
    notes,
    receipts,
    initialDelete,
  ]);

  const handleExpenseDate = (date) => {
    setDate(date);
    let expenseYear = date?.split("-")[0];
    setYear(expenseYear);
  };

  const toggleTagEntry = () => {
    setAddingTags(!addingTags);
    if (!addingTags) {
      setTimeout(() => {
        document.getElementById("expense-tag-input").focus();
      }, 25);
    }
  };

  const addTag = (tag) => {
    if (tag.trim() && tags.length < 10) {
      setTags([...tags, tag.trim()]);
      tagRef.current.value = "";
    }
    let el = document.querySelector(".add-tag-plus-btn");
    // checks value one less than desired max tag count to account for count lag.
    if (tags.length > 8) {
      if (el) {
        el.disabled = true;
      }
    } else {
      if (el) {
        el.disabled = false;
      }
    }
  };

  const handleEnter = (e) => {
    if (e.key === "Enter") {
      addTag(tagRef.current.value, tags.length);
    }
  };

  const deleteTag = (index) => {
    let currentTags = [...tags];
    currentTags.splice(index, 1);
    setTags(currentTags);
    document.querySelector(".add-tag-plus-btn").disabled = false;
  };

  const renderTags = () => {
    return (
      <>
        {tags.map((tag, index) => {
          return (
            <sl-badge variant="primary" key={index} pill>
              {tag} <IoCloseCircle onClick={() => deleteTag(index)} />
            </sl-badge>
          );
        })}
      </>
    );
  };

  const getBase64 = (file) => {
    let reader = new FileReader();
    let ts = new Date();
    reader.readAsDataURL(file);
    reader.onload = function () {
      let currentReceipts = [...receipts];
      let newReceipt = {
        name: file.name,
        type: file.type.includes("pdf") ? "pdf" : "image",
        base64: reader.result, // Base64 string of the image
        file: file,
      };
      currentReceipts.push(newReceipt);
      setReceipts(currentReceipts);
      // Input key sets a random (snapshot in time) value to clear out the existing
      // fileReader file, allows reupload of the same picture if necessary.
      setInputKey(ts);
    };
    reader.onerror = function (error) {
      console.error("Error: ", error);
    };
  };

  const getPlaceholderImage = (file) => {
    let ts = new Date();
    let currentReceipts = [...receipts];
    let newReceipt = {
      name: file.name,
      type: "pdf",
      base64:
        "data:image/png;base64,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",
      file: file,
    };
    currentReceipts.push(newReceipt);
    setReceipts(currentReceipts);
    // Input key sets a random (snapshot in time) value to clear out the existing
    // fileReader file, allows reupload of the same picture if necessary.
    setInputKey(ts);
  };

  const selectFile = (file) => {
    if (file) {
      const MAX_FILE_SIZE = 5 * 1024 * 1024;
      if (file.size > MAX_FILE_SIZE) {
        setFileError("File size is too large. Please try another file smaller than 5MB.");
      } else {
        if (
          file.type !== "image/jpg" &&
          file.type !== "image/jpeg" &&
          file.type !== "image/png" &&
          file.type !== "application/pdf"
        ) {
          setFileError("File type must be a .jpg, .jpeg, .png, or .pdf");
        } else {
          setFileError("");
          if (file.type.includes("pdf")) {
            getPlaceholderImage(file);
          } else {
            getBase64(file);
          }
        }
      }
    }
  };

  const deleteReceipt = (index) => {
    let currentReceipts = [...receipts];
    currentReceipts.splice(index, 1);
    setReceipts(currentReceipts);
  };

  const renderReceipts = () => {
    let pageHeight = window.innerHeight - 150;
    let pageWidth = window.innerWidth - 30;
    return (
      <>
        {receipts.map((receipt, index) => {
          return (
            <div
              className={
                expandedReceipt === receipt.filename + "-" + index ? "expense-receipt expanded" : "expense-receipt"
              }
              key={receipt.filename + "-" + index}
              onClick={() => {
                if (expandedReceipt === receipt.filename + "-" + index) {
                  setExpandedReceipt("");
                }
              }}
            >
              {expandedReceipt === receipt.filename + "-" + index ? null : (
                <IoCloseCircle className="remove-img-icon" onClick={() => deleteReceipt(index)} />
              )}
              {expandedReceipt === "" ? (
                <BsArrowsAngleExpand
                  className="expand-img-icon"
                  onClick={() => setExpandedReceipt(receipt.filename + "-" + index)}
                />
              ) : null}
              {receipt.type === "pdf" ? (
                expandedReceipt === receipt.filename + "-" + index ? (
                  <Document
                    file={receipt.downloadLink ? receipt.downloadLink : receipt.file}
                    className="pdf-thumbnail-wrapper"
                  >
                    <Thumbnail
                      className="thumbnail-page"
                      height={pageHeight}
                      width={pageWidth > pageHeight ? null : pageWidth}
                      pageIndex={0}
                    />
                  </Document>
                ) : (
                  <Document
                    file={receipt.downloadLink ? receipt.downloadLink : receipt.file}
                    className="pdf-thumbnail-wrapper"
                  >
                    <Thumbnail className="thumbnail-page" height={183} pageIndex={0} />
                  </Document>
                )
              ) : (
                <img
                  src={receipt.downloadLink ? receipt.downloadLink : receipt.base64}
                  onClick={() => {
                    if (!expandedReceipt) {
                      setExpandedReceipt(receipt.filename + "-" + index);
                    } else {
                      if (expandedReceipt === receipt.filename + "-" + index) {
                        setExpandedReceipt("");
                      } else {
                        setExpandedReceipt(receipt.filename + "-" + index);
                      }
                    }
                  }}
                />
              )}

              {receipt.type === "pdf" && expandedReceipt === receipt.filename + "-" + index ? (
                <div className="pdf-title">{receipt.name}</div>
              ) : null}
            </div>
          );
        })}
      </>
    );
  };

  const validateDate = (date, source) => {
    if (source === "withdrawn") {
      if (date > currentDate) {
        setWithdrawnDateError("Oops! You've set a future date; only current & past withdrawals are allowed.");
      } else if (date < minDate) {
        setWithdrawnDateError("Oops! You can only add expenses up to 1/1/2004 and forward.");
      } else {
        setDateWithdrawn(date);
        setWithdrawnDateError("");
      }
    } else {
      if (date > maxDate) {
        setExpenseDateError(
          "Oops! You've set a date too far the future, only expenses within the next 6 months can be added."
        );
      } else if (date < minDate) {
        setExpenseDateError("Oops! You can only add expenses up to 1/1/2004 and forward.");
      } else {
        setExpenseDateError("");
      }
    }
  };

  const validateAmount = (amount, event) => {
    let value;
    if (event === "blur") {
      value = currencyInputHandling(amount, "blur");
    } else {
      value = currencyInputHandling(amount);
    }

    if (currencyDeformatter(value) < 0.01) {
      setAmountError("Oops! Please set a value greater than $0.00.");
    } else if (currencyDeformatter(value) > 250000) {
      setAmountError("Oops! Only expenses under $250,000 are allowed. Please try an updated value.");
    } else {
      setAmountError("");
    }

    setAmount(value);
  };

  return (
    <sl-drawer placement="bottom" class="shoebox-drawer" id="expense-form-drawer">
      <span slot="label" class="drawer-header">
        <h1>{selectedExpense ? "Edit Expense" : "Add Expense"}</h1>
        <AiFillPlusCircle
          id="close-expense-form-button"
          onClick={() => {
            if (selectedExpense) {
              navigate(`/expenses/details/${selectedExpense.id}`);
            } else {
              navigate("/expenses");
            }
            resetForm();
            document.getElementById("expense-form-drawer")?.hide();
            if (selectedExpense) {
              mapExpenseValues(selectedExpense);
            }
          }}
        />
      </span>
      <div className="expense-form-wrapper" ref={expenseFormRef}>
        <section className="expense-form">
          <span className="name-block">
            <label>Name</label>
            <input
              type="text"
              onChange={(e) => setName(e.target.value)}
              value={name}
              maxLength="85"
              disabled={addExpenseLoading || updateExpenseLoading ? true : null}
            ></input>
          </span>
          <div className={expenseDateError || amountError ? "form-error-wrapper shown" : "form-error-wrapper"}>
            <span className="expense-form-error">
              {!expenseDateError ? (amountError ? amountError : null) : expenseDateError}
            </span>
          </div>
          <div className="date-amount-block">
            <span>
              <label>Date</label>
              <input
                disabled={addExpenseLoading || updateExpenseLoading ? true : null}
                type="date"
                min={minDate}
                max={maxDate}
                onBlur={() => validateDate(date, "expense")}
                onChange={(e) => handleExpenseDate(e.target.value)}
                value={date ? date : ""}
              ></input>
            </span>
            <span>
              <label>Amount</label>
              <span className="blocks-currency-input">
                <span className={addExpenseLoading || updateExpenseLoading ? "disabled" : null}>$</span>
                <input
                  disabled={addExpenseLoading || updateExpenseLoading ? true : null}
                  className="expense-amount-input"
                  onClick={(e) => e.target.select()}
                  onChange={(e) => validateAmount(e.target.value)}
                  onBlur={(e) => {
                    if (e.target.value !== "") {
                      validateAmount(e.target.value, "blur");
                    }
                  }}
                  value={amount}
                ></input>
              </span>
            </span>
          </div>
          <sl-select
            disabled={addExpenseLoading || updateExpenseLoading ? true : null}
            placeholder="Select"
            id="expense-category-input"
            value={category}
            label="Category"
            clearable
            filled
          >
            {expenseCategories.map((category) => {
              return (
                <sl-option
                  key={category.toLowerCase().split(" ").join("-")}
                  value={category.toLowerCase().split(" ").join("-")}
                  onClick={() => {
                    setCategory(category.toLowerCase().split(" ").join("-"));
                  }}
                >
                  {category}
                </sl-option>
              );
            })}
          </sl-select>
          <div className="hsa-eligible-block">
            <sl-radio-group label="HSA Eligible?" id="expense-eligibility-input" value={eligible}>
              <sl-radio-button
                onClick={() => setEligible(true)}
                value={true}
                disabled={addExpenseLoading || updateExpenseLoading ? true : null}
              >
                Yes
              </sl-radio-button>
              <sl-radio-button
                onClick={() => {
                  setEligible(false);
                  setWithdrawn(false);
                  setWithdrawnDateError("");
                  setDateWithdrawn("");
                }}
                value={false}
                disabled={addExpenseLoading || updateExpenseLoading ? true : null}
              >
                No
              </sl-radio-button>
            </sl-radio-group>
            {hasGuide && (
              <span className="not-sure-link">
                <p>Not Sure?</p>
                <a href={hsaExpenseGuide[0].url} target="_blank">
                  Check out this guide <FiExternalLink />
                </a>
              </span>
            )}
          </div>
          <label className="insurance-claim-label">Claim Under Insurance?</label>
          <div className="insurance-claim-block">
            <sl-radio-group value={underInsurance}>
              <sl-radio-button
                value={true}
                onClick={() => setUnderInsurance(true)}
                disabled={addExpenseLoading || updateExpenseLoading ? true : null}
              >
                Yes
              </sl-radio-button>
              <sl-radio-button
                disabled={addExpenseLoading || updateExpenseLoading ? true : null}
                value={false}
                onClick={() => {
                  setUnderInsurance(false);
                  setInsuranceToggle(false);
                  setSelectedInsurance();
                  setSelectedLogo();
                }}
              >
                No
              </sl-radio-button>
            </sl-radio-group>
            {underInsurance && (
              <sl-button
                class="expense"
                onClick={() => setInsuranceToggle(!insuranceToggle)}
                variant={insuranceToggle ? "default" : "neutral"}
                disabled={addExpenseLoading || updateExpenseLoading ? true : null}
              >
                {!insuranceToggle ? (selectedInsurance ? "Update Insurance" : "Select Insurance") : "Set Insurance"}
              </sl-button>
            )}
          </div>
          {!insuranceToggle && selectedInsurance && (
            <div className="selected-insurance-display">
              <small>Selected Insurance: </small>
              {isCustomInsurance ? (
                <span className="manual-selection-display">{selectedInsurance}</span>
              ) : (
                <img
                  className={
                    selectedInsurance !== "aetna" && selectedInsurance !== "bcbs" && selectedInsurance !== "humana"
                      ? selectedInsurance !== "harvard" && selectedInsurance !== "centene"
                        ? "small"
                        : "medium"
                      : ""
                  }
                  src={selectedLogo}
                />
              )}
            </div>
          )}
          <div
            className={underInsurance && insuranceToggle ? "insurance-option-tiles expanded" : "insurance-option-tiles"}
          >
            <div className="button-row">
              <div
                id="blocks-icon-button"
                onClick={() => (selectedInsurance === "aetna" ? pickInsurance() : pickInsurance("aetna"))}
                className={selectedInsurance === "aetna" ? "insurance-tile selected" : "insurance-tile"}
                variant="neutral"
              >
                <img src={insuranceAssetProvider("aetna", colorMode)} />
              </div>
              <div
                id="blocks-icon-button"
                onClick={() => (selectedInsurance === "bcbs" ? pickInsurance() : pickInsurance("bcbs"))}
                className={selectedInsurance === "bcbs" ? "insurance-tile selected" : "insurance-tile"}
                variant="neutral"
              >
                <img src={insuranceAssetProvider("bcbs", colorMode)} />
              </div>
            </div>
            <div className="button-row">
              <div
                id="blocks-icon-button"
                onClick={() => (selectedInsurance === "uhc" ? pickInsurance() : pickInsurance("uhc"))}
                className={selectedInsurance === "uhc" ? "insurance-tile selected" : "insurance-tile"}
                variant="neutral"
              >
                <img className="small" src={insuranceAssetProvider("uhc", colorMode)} />
              </div>
              <div
                id="blocks-icon-button"
                onClick={() => (selectedInsurance === "cigna" ? pickInsurance() : pickInsurance("cigna"))}
                className={selectedInsurance === "cigna" ? "insurance-tile selected" : "insurance-tile"}
                variant="neutral"
              >
                <img className="small" src={insuranceAssetProvider("cigna", colorMode)} />
              </div>
            </div>
            <div className="button-row">
              <div
                id="blocks-icon-button"
                onClick={() => (selectedInsurance === "humana" ? pickInsurance() : pickInsurance("humana"))}
                className={selectedInsurance === "humana" ? "insurance-tile selected" : "insurance-tile"}
                variant="neutral"
              >
                <img src={insuranceAssetProvider("humana", colorMode)} />
              </div>
              <div
                id="blocks-icon-button"
                onClick={() => (selectedInsurance === "kaiser" ? pickInsurance() : pickInsurance("kaiser"))}
                className={selectedInsurance === "kaiser" ? "insurance-tile selected" : "insurance-tile"}
                variant="neutral"
              >
                <img className="small" src={insuranceAssetProvider("kaiser", colorMode)} />
              </div>
            </div>
            <div className="button-row">
              <div
                id="blocks-icon-button"
                onClick={() => (selectedInsurance === "harvard" ? pickInsurance() : pickInsurance("harvard"))}
                className={selectedInsurance === "harvard" ? "insurance-tile selected" : "insurance-tile"}
                variant="neutral"
              >
                <img className="medium" src={insuranceAssetProvider("harvard", colorMode)} />
              </div>
              <div
                id="blocks-icon-button"
                onClick={() => (selectedInsurance === "centene" ? pickInsurance() : pickInsurance("centene"))}
                className={selectedInsurance === "centene" ? "insurance-tile selected" : "insurance-tile"}
                variant="neutral"
              >
                <img className="medium" src={insuranceAssetProvider("centene", colorMode)} />
              </div>
            </div>
            <input
              placeholder="Other (please specify)"
              id="custom-ins-input"
              onChange={(e) => handleManualInsuranceEntry(e.target.value)}
            ></input>
          </div>
          {eligible ? (
            <section>
              <label className="withdrawn-expense-label">Withdrawn from HSA Balance?</label>
              <div className={widthdrawnDateError && withdrawn ? "form-error-wrapper shown" : "form-error-wrapper"}>
                <span className="expense-form-error">{widthdrawnDateError}</span>
              </div>
              <div className="withdrawn-expense-block">
                <sl-radio-group value={withdrawn}>
                  <sl-radio-button
                    value={true}
                    onClick={() => setWithdrawn(true)}
                    disabled={addExpenseLoading || updateExpenseLoading ? true : null}
                  >
                    Yes
                  </sl-radio-button>
                  <sl-radio-button
                    value={false}
                    onClick={() => {
                      setWithdrawn(false);
                      setWithdrawnDateError("");
                      setDateWithdrawn("");
                    }}
                    disabled={addExpenseLoading || updateExpenseLoading ? true : null}
                  >
                    No
                  </sl-radio-button>
                </sl-radio-group>
                {withdrawn && (
                  <input
                    type="date"
                    min={minDate}
                    max={currentDate}
                    onBlur={() => validateDate(dateWithdrawn, "withdrawn")}
                    onChange={(e) => setDateWithdrawn(e.target.value)}
                    value={dateWithdrawn}
                    disabled={addExpenseLoading || updateExpenseLoading ? true : null}
                  ></input>
                )}
              </div>
            </section>
          ) : null}

          <div></div>
          <label>
            Tags & Receipts <span className="optional-indicator">optional</span>
          </label>
          <div className={fileError ? "form-error-wrapper shown" : "form-error-wrapper"}>
            <span className="expense-form-error">{fileError}</span>
          </div>
          <div className={addingTags ? "add-tags-state" : "button-row"}>
            {/* For blocks-icon-buttons, you need to add an additional dynamic class of disabled to correctly target and change the color
            The disabled attribute is also needed to handle correct cursor functionality */}
            <sl-button
              id="blocks-icon-button"
              onClick={() => toggleTagEntry()}
              class={
                addExpenseLoading || updateExpenseLoading ? "expense add-tags-btn disabled" : "expense add-tags-btn"
              }
              style={addingTags ? { width: "117px" } : { width: "100%", maxWidth: "300px" }}
              variant="neutral"
              disabled={addExpenseLoading || updateExpenseLoading ? true : null}
            >
              {addingTags ? <HiOutlineArrowLeft /> : <FaHashtag />}
              <span>{addingTags ? "All Done" : "Add Tags"}</span>
            </sl-button>
            {addingTags ? (
              <input ref={tagRef} id="expense-tag-input" onKeyDown={handleEnter} maxLength={30}></input>
            ) : (
              <div
                className={
                  addExpenseLoading || updateExpenseLoading
                    ? "styled-input-blocks-button disabled"
                    : "styled-input-blocks-button"
                }
                id="add-reciept-btn"
              >
                <label>
                  <input
                    type="file"
                    accept="image/jpg, image/jpeg, image/png, application/pdf"
                    onChange={(e) => selectFile(e.target.files[0])}
                    key={inputKey}
                  />
                  <span>
                    <TbReceiptFilled />{" "}
                    {receipts.length < 5 ? (
                      "Add Reciept"
                    ) : (
                      <span className="receipt-max">
                        Max <TbSquareRoundedNumber5 /> Allowed
                      </span>
                    )}
                  </span>
                </label>
              </div>
            )}
            {addingTags && (
              <sl-button class="add-tag-plus-btn" onClick={() => addTag(tagRef.current.value)}>
                <AiFillPlusCircle />
              </sl-button>
            )}
          </div>
          <div className="tag-list">{renderTags()}</div>
          <div className="receipt-list" id="receipt-list-container">
            {renderReceipts()}
          </div>
          <span className="notes-block">
            <label>
              Notes <span className="optional-indicator">optional</span>
            </label>
            <textarea
              onChange={(e) => setNotes(e.target.value)}
              value={notes}
              maxLength={1200}
              disabled={addExpenseLoading || updateExpenseLoading ? true : null}
            ></textarea>
          </span>
        </section>
      </div>
      {selectedExpense ? (
        <div slot="footer" className={initialDelete ? "edit-drawer-footer deleting" : "edit-drawer-footer"}>
          <sl-button
            variant="neutral"
            class="update-cancel-action"
            onClick={() => {
              initialDelete ? setInitialDelete(false) : handleExpenseAction("update");
            }}
            id={!initialDelete && "update-expense-button"}
            disabled={updateExpenseLoading || deleteExpenseLoading ? true : null}
          >
            {!initialDelete ? (
              updateExpenseLoading ? (
                <sl-spinner variant="button-neutral"></sl-spinner>
              ) : (
                "Update"
              )
            ) : (
              <IoClose />
            )}
          </sl-button>
          <sl-button
            class="shoebox-drawer-action-button"
            onClick={() => {
              initialDelete ? handleExpenseAction("delete") : setInitialDelete(true);
            }}
            id="close-edit-btn"
            variant="danger"
            disabled={updateExpenseLoading || deleteExpenseLoading ? true : null}
          >
            {initialDelete ? (
              deleteExpenseLoading ? (
                <sl-spinner variant="button-danger"></sl-spinner>
              ) : (
                "Confirm Delete"
              )
            ) : (
              "Delete"
            )}
          </sl-button>
        </div>
      ) : (
        <sl-button
          slot="footer"
          class="save-action"
          disabled={addExpenseLoading ? true : null}
          onClick={() => handleExpenseAction("save")}
        >
          {addExpenseLoading ? <sl-spinner variant="button-primary"></sl-spinner> : "Save"}
        </sl-button>
      )}
    </sl-drawer>
  );
};

export { ExpenseForm };
